import { useEffect, useMemo, useState } from "react";
import {
  List,
  TextField,
  DatagridConfigurable,
  NumberField,
  useTheme,
  usePermissions,
  useTranslate,
  Button,
  useListContext,
} from "react-admin";
import saleRankingFilterList from "../../components/saleRankings/filter/saleRankingFilterList";
import DownloadAction from "../../components/tableListActions/DownloadActionNew";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";
import SaleRankingExpandList from "../../components/saleRankings/expand/SaleRankingExpandList";
import ReplyIcon from "@mui/icons-material/Reply";
import { useFormContext } from "react-hook-form";
import useLastPeriodRange from "../../components/hooks/useLastPeriodRange";
import { differenceInDays } from "date-fns";
import ManualReportList from "../../components/reports/ManualReportList";
import useCompanyScopedListFilters from "../../hooks/useCompanyScopedListFilters";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();

const proceedSelected = (
  sources,
  setOpenSaleRanking,
  selectedIds,
  onUnselectItems,
  data,
  setValue,
  filterValues,
  productNameSource
) => {
  for (let i = 0; i < data.length; i++) {
    const elm = data[i];
    if (elm.id === selectedIds[0]) {
      const buffMax = +elm.average_sales_per_month * 1.5;
      setValue(sources[0], elm.id, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      if (productNameSource) {
        setValue(productNameSource, elm.product_name || "", {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
      setValue(sources[1], elm.average_sales_per_month, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(sources[2], elm.remaining_stock, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(sources[3], elm.average_sales_per_month, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(sources[4], buffMax, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(
        sources[5],
        +elm.average_sales_per_month - +elm.remaining_stock,
        {
          shouldDirty: true,
          shouldTouch: true,
        }
      );
      setValue(sources[6], buffMax - +elm.remaining_stock, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(sources[7], "", {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(sources[8], "", {
        shouldDirty: true,
        shouldTouch: true,
      });

      const startDate = new Date(filterValues.start_date);
      const endDate = new Date(filterValues.end_date);

      setValue(sources[9], differenceInDays(endDate, startDate), {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }
  setOpenSaleRanking(false);
  onUnselectItems();
};

const SaleRankingForRestockPlanBulkAction = ({
  sources,
  setOpenSaleRanking,
  productNameSource,
}) => {
  const { selectedIds, onUnselectItems, data, filterValues } = useListContext();
  const { setValue } = useFormContext();
  const handleClick = () => {
    proceedSelected(
      sources,
      setOpenSaleRanking,
      selectedIds,
      onUnselectItems,
      data,
      setValue,
      filterValues
      ,productNameSource
    );
  };
  return (
    <Button label="mine.label.select" onClick={handleClick}>
      <ReplyIcon />
    </Button>
  );
};

const DataGridKecopret = ({
  resource,
  sources,
  openSaleRanking,
  setOpenSaleRanking,
  productNameSource,
}) => {
  const t = useTranslate();
  const [theme] = useTheme("light");
  const { permissions } = usePermissions();
  const { selectedIds, onSelect, onUnselectItems, data, filterValues } =
    useListContext();
  const { setValue } = useFormContext() ?? {};
  const [lastClick, setLastClick] = useState(0);

  useEffect(() => {
    if (!openSaleRanking) {
      // JIKA DIALOG CLOSE
      onUnselectItems();
      // JIKA DIALOG CLOSE
    }
  }, [openSaleRanking]);

  useEffect(() => {
    // UNTUK HANDLE HANYA BOLEH SELECT 1 ROW, PILIH YG DI SELECT TERAKHIR
    if (selectedIds.length) {
      onSelect([selectedIds[selectedIds.length - 1]]);
    }
    // UNTUK HANDLE HANYA BOLEH SELECT 1 ROW, PILIH YG DI SELECT TERAKHIR
  }, [selectedIds]);

  const handleRowClick = (id, resource, record) => {
    const currentTime = new Date().getTime();

    if (currentTime - lastClick < 300) {
      // It's a double-click
      proceedSelected(
        sources,
        setOpenSaleRanking,
        selectedIds,
        onUnselectItems,
        data,
        setValue,
        filterValues,
        productNameSource
      );
      return false;
    } else {
      // It's a single click
      setLastClick(currentTime);
      return !!resource ? "toggleSelection" : "expand";
    }
  };

  return (
    <DatagridConfigurable
      bulkActionButtons={
        resource ? (
          <SaleRankingForRestockPlanBulkAction
          sources={sources}
          setOpenSaleRanking={setOpenSaleRanking}
          productNameSource={productNameSource}
          />
        ) : (
          false
        )
      }
      isRowSelectable={() => true}
      rowClick={handleRowClick}
      expand={<SaleRankingExpandList />}
      expandSingle={!!resource}
      sx={{
        "& .RaDatagrid-expandedPanel > td": {
          p: 0,
          bgcolor: theme !== "dark" ? "#E7EBF0" : "grey.800",
        },
      }}
    >
      {permissions && permissions.update_company ? (
        <TextField label="mine.label.company" source="company.alias" />
      ) : null}
      <TextField source="code" label="mine.label.product_code" />
      <NumberField
        source="months_sold"
        label="mine.label.effective_month"
        textAlign="center"
      />
      <NumberField
        source="ranking_total_profit_per_brand"
        label="Rank Profit Brand"
        textAlign="center"
      />
      <NumberField
        source="ranking_total_profit"
        label="Rank Profit"
        textAlign="center"
      />
      <NumberField
        source="ranking_quantity_sold"
        label="Rank Quantity"
        textAlign="center"
      />
      <NumberField source="total_quantity_sold" label="Total Quantity" />
      <NumberField
        source="total_sale"
        label={"Total " + t("mine.expand_menu.sale")}
      />
      <NumberField source="total_profit" label="Total Profit" />
      <NumberField
        source="total_average_profit"
        label={"Total " + t("mine.label.profit_average")}
      />
      <NumberField source="average_sales_per_month" label="Avg Sales / Bln" />
      <NumberField
        source="remaining_stock"
        label="mine.label.remaining_stock"
      />
    </DatagridConfigurable>
  );
};
const SaleRankingList = ({
  resource,
  sources,
  openSaleRanking,
  setOpenSaleRanking,
  productNameSource,
}) => {
  // kalau di restock plan startDate akan dirubah, kalau di sale ranking dua2nya apa adanya tidak dirubah
  const [startDate, endDate] = useLastPeriodRange(firstDate, lastDate);
  // kalau di restock plan startDate akan dirubah, kalau di sale ranking dua2nya apa adanya tidak dirubah
  const { permissions, filterDefaultValues } = useCompanyScopedListFilters({
    start_date: startDate,
    end_date: endDate,
  });

  const list = useMemo(() => {
    const ReportList = resource ? List : ManualReportList;
    return (
      <ReportList
        // jika resource ada isinya tandanya untuk di form restock plan
        resource={resource ? resource : undefined}
        storeKey={resource ? false : undefined}
        disableSyncWithLocation
        title="mine.list_title.sale_ranking"
        empty={false}
        actions={<DownloadAction field="total_profit" order="DESC" />}
        filters={saleRankingFilterList(permissions, resource)}
        filterDefaultValues={filterDefaultValues}
        requiredFilters={["start_date", "end_date"]}
        queryOptions={{ meta: { sort: ["total_profit DESC"] } }} // total_profit adalah kolom hasil SUM(), bukan kolom di database
        sx={{
          flexDirection: "column",
        }}
      >
        <DataGridKecopret
          resource={resource}
          sources={sources}
          openSaleRanking={openSaleRanking}
          setOpenSaleRanking={setOpenSaleRanking}
          productNameSource={productNameSource}
        />
      </ReportList>
    );
  }, [openSaleRanking, startDate, endDate, permissions, filterDefaultValues]);
  return list;
};

export default SaleRankingList;
